import type { ReactNode } from 'react';
import clsx from 'clsx';
import { AlertTriangle, Inbox } from 'lucide-react';
import { Button } from './Button';
import { isApiError } from '@/utils/ApiError';

/**
 * The three states every data view needs besides "has data".
 *
 * Provided as components so no screen invents its own, and so an empty result
 * never renders as a blank rectangle the user cannot interpret.
 */

/** A shimmering placeholder block. Width is a Tailwind class, e.g. `w-32`. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('skeleton h-4', className)} aria-hidden="true" />;
}

/**
 * Table skeleton.
 *
 * Preferred over a spinner for tables: it holds the layout at roughly the size
 * the real content will be, so the page does not jump when data lands, and it
 * communicates *what* is loading rather than merely *that* something is.
 */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="px-4 py-3" aria-busy="true" aria-label="Loading records">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex items-center gap-4 border-b border-line/60 py-3 last:border-0"
          style={{ opacity: 1 - rowIndex * 0.11 }}
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={columnIndex === 0 ? 'w-1/5' : columnIndex === 1 ? 'w-1/4' : 'w-1/6'}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 px-6 py-16 text-sm text-muted">
      <span className="skeleton h-4 w-4" />
      {label}…
    </div>
  );
}

export function EmptyState({ title, description, action, icon }: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="animate-fade-in px-6 py-16 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-3 text-subtle">
        {icon ?? <Inbox className="h-6 w-6" aria-hidden="true" />}
      </div>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {description !== undefined && (
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted">{description}</p>
      )}
      {action !== undefined && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Renders a failure honestly: the server's own message when there is one, and
 * the request id, which is what makes a user report traceable in the logs.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = isApiError(error)
    ? error.message
    : 'Something went wrong while loading this data.';
  const requestId = isApiError(error) ? error.requestId : undefined;

  return (
    <div className="animate-fade-in px-6 py-16 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger">
        <AlertTriangle className="h-6 w-6" aria-hidden="true" />
      </div>
      <h3 className="text-sm font-semibold text-ink">Unable to load</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">{message}</p>
      {requestId !== undefined && (
        <p className="mt-2 font-mono text-xs text-subtle">Reference: {requestId}</p>
      )}
      {onRetry !== undefined && (
        <div className="mt-5 flex justify-center">
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}
